import { App } from "../ui";
import type { ModalFuncProps } from "../ui";
import { useMemo } from "react";
import type { ReactNode } from "react";
import { isGlobalApiErrorActive } from "./GlobalApiErrorProvider";
import { usePersistentErrorFeedback } from "./persistentErrorFeedbackContext";

/**
 * Confirm-Optionen. `title` (und optional `content`) bereits übersetzt übergeben —
 * analog zum i18n-Vertrag der Layout-Komponenten.
 */
export type ConfirmOptions = Omit<ModalFuncProps, "onOk"> & {
  onOk?: () => void | Promise<void>;
};

declare global {
  var __mqorvaReportFeedbackError: undefined | ((source: string, args: unknown[]) => void);
}

const reportFeedbackError = (source: string, args: unknown[]) => {
  try {
    globalThis.__mqorvaReportFeedbackError?.(source, args);
  } catch {
    // Feedback logging must never interfere with the user-facing toast.
  }
};

const duplicateErrorToastWindowMs = 5000;
let lastVisibleErrorKey = "";
let lastVisibleErrorAt = 0;

const visibleErrorKey = (content: ReactNode) => {
  if (typeof content === "string" || typeof content === "number" || typeof content === "boolean") return String(content);
  if (content && typeof content === "object" && "toString" in content) return String(content);
  return "unknown";
};

const isDuplicateVisibleError = (content: ReactNode) => {
  const key = visibleErrorKey(content);
  const now = Date.now();
  if (key === lastVisibleErrorKey && now - lastVisibleErrorAt < duplicateErrorToastWindowMs) return true;
  lastVisibleErrorKey = key;
  lastVisibleErrorAt = now;
  return false;
};

/**
 * Einheitlicher Zugang zu Toasts/Notifications/Confirms für mQorva-Frontends.
 *
 * Wrappt `App.useApp()` statt der statischen `message.*` / `Modal.*`-Aufrufe.
 * In AntD v6 sind die statischen Varianten ohne `<App>`-Kontext nicht theme-
 * und locale-gebunden — `useFeedback()` setzt deshalb voraus, dass die App in
 * einen `<App>`-Provider (antd) gewickelt ist (passiert im Roll-out).
 *
 * Lokalisierung bleibt beim Aufrufer: Texte werden bereits übersetzt übergeben
 * (z. B. `feedback.success(tr("Saved"))`). Der Hook führt selbst kein `tr`.
 *
 * @example
 * ```tsx
 * const feedback = useFeedback();
 * feedback.success(tr("Saved"));
 * feedback.error(getDisplayErrorMessage(err, "Failed to save", tr));
 * feedback.confirmDelete({
 *   title: tr("Delete entry?"),
 *   content: tr("This cannot be undone."),
 *   okText: tr("Delete"),
 *   cancelText: tr("Cancel"),
 *   onOk: () => remove(id),
 * });
 * ```
 */
export function useFeedback() {
  const { message, notification, modal } = App.useApp();
  const persistentErrorFeedback = usePersistentErrorFeedback();

  return useMemo(() => {
    const confirm = (opts: ConfirmOptions) =>
      modal.confirm({
        centered: true,
        keyboard: true,
        autoFocusButton: "ok",
        ...opts,
      });

    /** Destruktive Bestätigung: roter OK-Button, Fokus auf „Abbrechen". */
    const confirmDelete = (opts: ConfirmOptions) =>
      modal.confirm({
        centered: true,
        keyboard: true,
        okType: "danger",
        autoFocusButton: "cancel",
        ...opts,
      });

    return {
      /** Erfolgs-Toast. Text bereits übersetzt übergeben. */
      success: (content: ReactNode, duration?: number) => message.success(content, duration),
      /** Error feedback; an optional provider keeps errors visible until dismissed. */
      error: (content: ReactNode, duration?: number) => {
        if (persistentErrorFeedback) {
          if (isGlobalApiErrorActive()) return Promise.resolve();
          // Reject late responses before reporting them under the new user/tenant context.
          if (persistentErrorFeedback(content, duration)) reportFeedbackError("antd.app.message.error", [content]);
          return Promise.resolve();
        }
        reportFeedbackError("antd.app.message.error", [content]);
        if (isGlobalApiErrorActive()) return Promise.resolve();
        if (isDuplicateVisibleError(content)) return Promise.resolve();
        return message.error(content, duration);
      },
      warning: (content: ReactNode, duration?: number) => message.warning(content, duration),
      info: (content: ReactNode, duration?: number) => message.info(content, duration),
      /** Reichhaltige Benachrichtigung (Titel + Beschreibung). */
      notification,
      /** Generische Bestätigung mit projektüblichen Defaults. */
      confirm,
      /** Destruktive Bestätigung (danger + Fokus auf Abbrechen). */
      confirmDelete,
      /** Direkter Durchgriff für Sonderfälle. */
      message,
      modal,
    };
  }, [message, notification, modal, persistentErrorFeedback]);
}

export type Feedback = ReturnType<typeof useFeedback>;
